import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import {
  useUnlockedKey,
  useEncryptOnSave,
  useCoachShareTargets,
} from "~/lib/crypto";
import { categoryQueryKeys } from "~/lib/queries/categoryQuery";
import { type CategoryFormData } from "~/lib/schemas/category-of-life";
import { createAndUploadCategoryFile } from "~/lib/upload-file";

import { CategoryForm } from "./-components/category-form";

const searchSchema = z.object({
  name: z.string().optional(),
});

export const Route = createFileRoute("/_user/life-design/new-category")({
  staticData: { title: "New Category" },
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { name } = Route.useSearch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const keyManager = useUnlockedKey();
  const { encryptFields } = useEncryptOnSave(keyManager);
  const coachShareTargets = useCoachShareTargets();

  const handleSubmit = async (data: CategoryFormData) => {
    if (!keyManager) {
      toast.error("Encryption is required. Please unlock your encryption key.");
      return;
    }

    setIsSubmitting(true);

    try {
      // The id is made here, and the category is created once, with the
      // keys of the coaches it is shared with.
      const categoryId = uuidv4();
      const encrypted = await encryptFields(
        { name: data.name, shortDescription: data.shortDescription ?? "" },
        await coachShareTargets("vision"),
      );
      await createAndUploadCategoryFile(
        {
          categoryId,
          encryptedName: encrypted.fields.name,
          encryptedShortDescription: encrypted.fields.shortDescription,
          iv: encrypted.iv,
          keyGrants: encrypted.keyGrants,
        },
        data.file || undefined,
      );

      await queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all });
      toast.success("Category created");
      navigate({ to: "/life-design" });
    } catch (error) {
      console.error("Failed to create category:", error);
      toast.error("Failed to create category. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: "/life-design" });
  };

  return (
    <div className="space-y-6">
      <CategoryForm
        initialData={
          name ? { name, shortDescription: "", file: null } : undefined
        }
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
